import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { fmt, colorByValue } from '../../utils/format';
import useLang from '../../hooks/useLang';
import { t } from '../../utils/i18n';

const pct = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
const large = (v) => fmt.large(v);
const growth = (v) => {
  if (v == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const color = v >= 0 ? 'var(--green)' : 'var(--red)';
  return <span style={{ color }}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>;
};

export default function FundamentalsPanel({ fundamentals }) {
  const { lang } = useLang();
  if (!fundamentals) return null;
  const { income, balance, cashflow, metrics } = fundamentals;

  const years = [...(income || [])].reverse();

  const revenueChart = years.map(i => ({ year: i.date, [t('revenue', lang)]: i.revenue ? i.revenue / 1e9 : null, [t('netIncome', lang)]: i.netIncome ? i.netIncome / 1e9 : null }));
  const marginChart = years.map(i => ({ year: i.date, [`${t('grossMargin', lang)} %`]: i.grossMargin ? +(i.grossMargin * 100).toFixed(1) : null, [`${t('netMargin', lang)} %`]: i.netMargin ? +(i.netMargin * 100).toFixed(1) : null, [`${t('opMarginShort', lang)} %`]: i.operatingMargin ? +(i.operatingMargin * 100).toFixed(1) : null }));

  return (
    <div>
      {/* YoY Comparison Table */}
      <div className="card">
        <div className="card-title">{t('annualFinancialComparison', lang)}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('metric', lang)}</th>
                {income.map(i => <th key={i.date}>{i.date}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: 'var(--bg-surface)' }}>
                <td style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }} colSpan={income.length + 1}>{t('incomeStatement', lang)}</td>
              </tr>
              <tr>
                <td>{t('revenue', lang)}</td>
                {income.map(i => <td key={i.date}>{large(i.revenue)}</td>)}
              </tr>
              <tr>
                <td>{t('revenueGrowth', lang)}</td>
                {income.map(i => <td key={i.date}>{growth(i.revenueGrowth)}</td>)}
              </tr>
              <tr>
                <td>{t('grossProfit', lang)}</td>
                {income.map(i => <td key={i.date}>{large(i.grossProfit)}</td>)}
              </tr>
              <tr>
                <td>{t('operatingIncome', lang)}</td>
                {income.map(i => <td key={i.date}>{large(i.operatingIncome)}</td>)}
              </tr>
              <tr>
                <td>{t('netIncome', lang)}</td>
                {income.map(i => <td key={i.date} style={{ color: i.netIncome > 0 ? 'var(--green)' : 'var(--red)' }}>{large(i.netIncome)}</td>)}
              </tr>
              <tr>
                <td>{t('netIncomeGrowth', lang)}</td>
                {income.map(i => <td key={i.date}>{growth(i.netIncomeGrowth)}</td>)}
              </tr>
              <tr>
                <td>{t('epsLabel', lang)}</td>
                {income.map(i => <td key={i.date}>{i.eps != null ? `$${Number(i.eps).toFixed(2)}` : '—'}</td>)}
              </tr>
              <tr style={{ background: 'var(--bg-surface)' }}>
                <td style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }} colSpan={income.length + 1}>{t('margins', lang)}</td>
              </tr>
              <tr>
                <td>{t('grossMargin', lang)}</td>
                {income.map(i => <td key={i.date} style={{ color: i.grossMargin > 0.3 ? 'var(--green)' : 'var(--yellow)' }}>{pct(i.grossMargin)}</td>)}
              </tr>
              <tr>
                <td>{t('operatingMargin', lang)}</td>
                {income.map(i => <td key={i.date} style={{ color: i.operatingMargin > 0.15 ? 'var(--green)' : 'var(--yellow)' }}>{pct(i.operatingMargin)}</td>)}
              </tr>
              <tr>
                <td>{t('netMargin', lang)}</td>
                {income.map(i => <td key={i.date} style={{ color: i.netMargin > 0 ? 'var(--green)' : 'var(--red)' }}>{pct(i.netMargin)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue & Net Income Chart */}
      <div className="card">
        <div className="card-title">{t('revenueNetIncome', lang)}</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={revenueChart} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="year" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
            <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6 }} formatter={(v) => v != null ? `$${v.toFixed(2)}B` : '—'} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey={t('revenue', lang)} fill="var(--accent)" radius={[4,4,0,0]} />
            <Bar dataKey={t('netIncome', lang)} fill="var(--green)" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Margin Trends */}
      <div className="card">
        <div className="card-title">{t('marginTrends', lang)}</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={marginChart}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="year" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
            <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} unit="%" />
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6 }} formatter={(v) => v != null ? `${v}%` : '—'} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={`${t('grossMargin', lang)} %`} stroke="var(--accent)" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey={`${t('opMarginShort', lang)} %`} stroke="var(--yellow)" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey={`${t('netMargin', lang)} %`} stroke="var(--green)" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Key Metrics */}
      <div className="card">
        <div className="card-title">{t('profitabilityReturns', lang)}</div>
        <div className="grid-4">
          <div className="stat-cell"><div className="stat-label">{t('grossMargin', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.grossMargin) }}>{pct(metrics?.grossMargin)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('opMarginShort', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.operatingMargin) }}>{pct(metrics?.operatingMargin)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('netMargin', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.netMargin) }}>{pct(metrics?.netMargin)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('roe', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.roe) }}>{pct(metrics?.roe)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('roa', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.roa) }}>{pct(metrics?.roa)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('evEbitda', lang)}</div><div className="stat-value">{fmt.multiple(metrics?.evToEbitda)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('revGrowth', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.revenueGrowthYoY) }}>{fmt.pct(metrics?.revenueGrowthYoY)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('epsGrowth', lang)}</div><div className="stat-value" style={{ color: colorByValue(metrics?.epsGrowthYoY) }}>{fmt.pct(metrics?.epsGrowthYoY)}</div></div>
        </div>
      </div>

      {/* Balance Sheet */}
      <div className="card">
        <div className="card-title">{t('balanceSheet', lang)}</div>
        <div className="grid-3">
          <div className="stat-cell"><div className="stat-label">{t('totalAssets', lang)}</div><div className="stat-value">{large(balance?.totalAssets)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('totalDebt', lang)}</div><div className="stat-value">{large(balance?.totalDebt)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('cash', lang)}</div><div className="stat-value">{large(balance?.cashAndEquivalents)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('equity', lang)}</div><div className="stat-value">{large(balance?.totalEquity)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('deRatio', lang)}</div><div className="stat-value" style={{ color: balance?.debtToEquity > 2 ? 'var(--red)' : 'var(--green)' }}>{fmt.multiple(balance?.debtToEquity)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('currentRatio', lang)}</div><div className="stat-value" style={{ color: balance?.currentRatio >= 1.5 ? 'var(--green)' : balance?.currentRatio >= 1 ? 'var(--yellow)' : 'var(--red)' }}>{fmt.multiple(balance?.currentRatio)}</div></div>
        </div>
      </div>

      {/* Cash Flow */}
      <div className="card">
        <div className="card-title">{t('cashFlow', lang)}</div>
        <div className="grid-4">
          <div className="stat-cell"><div className="stat-label">{t('operatingCF', lang)}</div><div className="stat-value" style={{ color: colorByValue(cashflow?.operatingCF) }}>{large(cashflow?.operatingCF)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('capex', lang)}</div><div className="stat-value">{large(cashflow?.capex)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('freeCashFlow', lang)}</div><div className="stat-value" style={{ color: colorByValue(cashflow?.freeCashFlow) }}>{large(cashflow?.freeCashFlow)}</div></div>
          <div className="stat-cell"><div className="stat-label">{t('dividendsPaid', lang)}</div><div className="stat-value">{large(cashflow?.dividendsPaid)}</div></div>
        </div>
      </div>
    </div>
  );
}
